import { FileSpreadsheet } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import type { DiagnosticScopeItemsTotals } from "@/api/diagnostic";
import { confidenceLabel } from "@/components/agents/functional/aderencia/aderenciaData";
import { OverviewCell } from "@/components/projects/diagnostic/acts/environment/EnvironmentOverviewCard";
import type { DiagnosticTabProps } from "@/components/projects/diagnostic/DiagnosticSection";
import { useProject } from "@/hooks/useApi";
import { useDiagnosticScopeItems } from "@/hooks/useDiagnosticScopeItems";
import { formatCount } from "@/lib/format";

import {
  NO_LOB,
  filterScopeItemRows,
  lobChips,
  lobOptions,
  scopeItemDetailRows,
  scopeItemsTile,
  type NoteFilter,
  type ScopeItemDetailRow,
} from "../data/scopeItems";
import { scopeItemsBasis } from "../processFit/scopeItemSource";
import { NO_VALUE } from "../sectionState";
import { downloadScopeItemsWorkbook } from "./exportScopeItems";
import { scopeBasisLabel, scopeItemsUnavailableReason } from "./scopeItemsText";

/** Os selects de filtro, com a altura e a borda da busca ao lado. */
const FILTER_CONTROL =
  "h-8 rounded-md border border-[#d4dae3] bg-white px-2 text-[12px] text-[#08254c] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c]";

/**
 * O detalhe somente leitura "Scope items ativos" do Ato 1 (EF-FUNC-004, #1344).
 *
 * Lê o `GET /diagnostic/scope-items` (#1506) na versão da aprovação que o tile
 * contou; com a curadoria salva, não há versão, e o servidor resolve a base pela
 * mesma regra do domínio. O endpoint conta com a mesma função do tile, então a
 * lista não diverge do número dele.
 *
 * As colunas são as da EF (RF-06), e a tela vai para o cliente: o endpoint só
 * manda a observação marcada para o relatório e não diz de onde veio a decisão
 * nem quem a tomou.
 */
export function ScopeItemsDetail({ validation, snapshotState }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const tile = scopeItemsTile(validation);
  const basis = scopeItemsBasis(validation);
  const { data, isLoading, refetch } = useDiagnosticScopeItems(projectId, basis);
  // O nome vai no arquivo do Excel; a página já leu o projeto, então vem do cache.
  const { data: project } = useProject(projectId);

  const [lob, setLob] = useState<string | undefined>(undefined);
  const [note, setNote] = useState<NoteFilter>("all");
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState("");
  const rows = useMemo(() => scopeItemDetailRows(data?.items ?? []), [data]);
  const lobs = useMemo(() => lobOptions(rows), [rows]);
  // Uma releitura com o filtro aplicado pode tirar a LoB escolhida das opções; o
  // select então mostraria "todas" enquanto a tabela seguisse filtrando por ela.
  const selectedLob = lob != null && lobs.includes(lob) ? lob : undefined;
  const shown = useMemo(
    () => filterScopeItemRows(rows, { lob: selectedLob, note, search }),
    [rows, selectedLob, note, search],
  );

  // O SheetJS carrega no primeiro clique; ocupado, o botão não baixa duas vezes.
  const exportWorkbook = async () => {
    setExporting(true);
    try {
      await downloadScopeItemsWorkbook(shown, t, project?.name);
    } finally {
      setExporting(false);
    }
  };

  // Sem o snapshot, o tile sairia "sem número" e a tela mandaria esperar a próxima
  // análise; carregando ou com a leitura falha, isso afirmaria o que não se sabe.
  if (snapshotState === "loading") {
    return (
      <p data-testid="scope-items-snapshot-loading" className="text-[13px] text-[#596b85]">
        {t("Loading…")}
      </p>
    );
  }
  if (snapshotState === "failed") {
    return (
      <p
        data-testid="scope-items-snapshot-error"
        className="rounded-lg border border-[#f3c7c2] bg-[#fdf3f2] px-4 py-3 text-[13px] text-[#a23b2f]"
      >
        {t("Could not load the diagnostic.")}
      </p>
    );
  }

  if (tile.kind !== "counted") {
    return (
      <p
        data-testid="scope-items-unavailable"
        className="rounded-lg border border-[#e3e8ef] bg-white px-4 py-3 text-[13px] text-[#596b85]"
      >
        {NO_VALUE} {scopeItemsUnavailableReason(tile, t)}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {isLoading ? (
        <p className="text-[13px] text-[#596b85]">{t("Loading…")}</p>
      ) : !data ? (
        // Falha não é lista vazia: "nenhum ativo" afirmaria algo sobre o projeto
        // que a tela não sabe. Só vale sem dado: numa releitura que falha, o
        // TanStack mantém a lista anterior, e trocá-la pelo erro jogaria fora o
        // filtro no meio da reunião.
        <div
          data-testid="scope-items-error"
          className="flex flex-wrap items-center gap-3 rounded-lg border border-[#f3c7c2] bg-[#fdf3f2] px-4 py-3 text-[13px] text-[#a23b2f]"
        >
          {t("Could not load the scope items.")}
          <button
            type="button"
            onClick={() => void refetch?.()}
            className="rounded-md border border-current px-3 py-1 text-[12px] font-semibold"
          >
            {t("Try again")}
          </button>
        </div>
      ) : data.state !== "ok" || !data.totals ? (
        // O tile contou num snapshot e a leitura do detalhe já não acha a base:
        // a tela diz por quê, em vez de "nenhum ativo". `ok` sem totais é
        // contrato quebrado, e indicadores zerados afirmariam o que não se sabe.
        <p
          data-testid="scope-items-unavailable"
          className="rounded-lg border border-[#e3e8ef] bg-white px-4 py-3 text-[13px] text-[#596b85]"
        >
          {NO_VALUE}{" "}
          {scopeItemsUnavailableReason(
            data.state === "empty"
              ? { kind: "empty" }
              : data.state === "ok"
                ? { kind: "failed" }
                : { kind: "unknown" },
            t,
          )}
        </p>
      ) : (
        <>
          <ScopeItemsIndicators
            totals={data.totals}
            basisLabel={scopeBasisLabel(
              { basis: data.basis ?? tile.basis, approvalVersion: data.approval_version ?? null },
              t,
            )}
          />

          <div className="rounded-lg border border-[#e3e8ef] bg-white">
            {/* Os dois selects e a busca, na ordem do protótipo. */}
            <div className="flex flex-wrap items-center gap-2 border-b border-[#eef1f5] p-3">
              <select
                value={selectedLob ?? ""}
                onChange={(event) => setLob(event.target.value || undefined)}
                aria-label={t("Filter by LoB")}
                className={FILTER_CONTROL}
              >
                <option value="">{t("All LoBs")}</option>
                {lobs.map((option) => (
                  <option key={option} value={option}>
                    {option === NO_LOB ? t("No LoB") : option}
                  </option>
                ))}
              </select>
              <select
                value={note}
                onChange={(event) => setNote(event.target.value as NoteFilter)}
                aria-label={t("Filter by note")}
                className={FILTER_CONTROL}
              >
                <option value="all">{t("With and without notes")}</option>
                <option value="with">{t("With notes")}</option>
                <option value="without">{t("Without notes")}</option>
              </select>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("Search by code or name")}
                aria-label={t("Search by code or name")}
                className="h-8 w-64 rounded-md border border-[#d4dae3] px-3 text-[12px] text-[#08254c] placeholder:text-[#9aa5b5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c]"
              />
              {/* RF-07: exporta as linhas da tabela, com os filtros aplicados. */}
              <button
                type="button"
                onClick={() => void exportWorkbook()}
                disabled={shown.length === 0 || exporting}
                aria-label={t("Export to Excel")}
                className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-md border border-[#d7dde6] bg-white px-3 text-[12px] font-semibold text-[#08254c] hover:bg-[#f5f8fc] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FileSpreadsheet aria-hidden className="h-3.5 w-3.5" />
                Excel
              </button>
            </div>
            <ScopeItemsTable
              rows={shown}
              emptyLabel={
                rows.length === 0 ? t("No active scope items.") : t("No items for the selected filters.")
              }
            />
            <p
              data-testid="scope-items-footnote"
              className="px-3 py-2.5 text-[11px] text-[#7b8799]"
            >
              {t("Confidence comes from the usage evidence in the ERP, not from the curation.")}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Os quatro indicadores do protótipo (RF-07), dos `totals` do endpoint, que saem
 * da mesma função que conta o tile: ativos + decisão + fora = avaliados.
 *
 * A evidência no ERP fica em dois números independentes, e não em "Fit (ERP) =
 * Ativar x ativados pelo consultor": os dois não somam os ativos, porque a
 * sugestão da árvore não lê o Fit (ERP) e o item de baseline entra ativo sem uso
 * medido.
 */
function ScopeItemsIndicators({
  totals,
  basisLabel,
}: {
  totals: DiagnosticScopeItemsTotals;
  basisLabel: string;
}) {
  const { t } = useTranslation();
  const chips = lobChips(totals);
  const card = "rounded-lg border border-[#e3e8ef] bg-white p-4";
  const detail = "mt-1 text-[11px] leading-snug text-[#7b8799]";
  return (
    <div
      data-testid="scope-items-indicators"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
    >
      <div className={card}>
        <OverviewCell
          testId="indicator-active"
          label={t("Active scope items")}
          value={formatCount(totals.active)}
          hint={t("of {{assessed}} assessed · {{outOfScope}} out of scope", {
            assessed: formatCount(totals.assessed),
            outOfScope: formatCount(totals.out_of_scope),
          })}
        >
          <div className={detail}>{basisLabel}</div>
        </OverviewCell>
      </div>
      <div className={card}>
        <OverviewCell
          testId="indicator-notes"
          label={t("With notes")}
          value={formatCount(totals.with_visible_note)}
          hint={t("{{count}} without notes", {
            count: totals.active - totals.with_visible_note,
          })}
        />
      </div>
      <div className={card}>
        <OverviewCell
          testId="indicator-erp"
          label={t("ERP evidence")}
          value={formatCount(totals.fit_erp_activate)}
          hint={t("with usage in the ERP")}
        >
          <div className={detail}>
            {t("{{count}} activated by consultant decision", {
              count: totals.activated_against_suggestion,
            })}
          </div>
        </OverviewCell>
      </div>
      <div data-testid="indicator-lobs" className={card}>
        <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
          {t("By line of business")}
        </div>
        {chips.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {chips.map((chip) => (
              <span
                key={chip.lob}
                data-testid="lob-chip"
                className="rounded-full bg-[#eef1f5] px-2 py-0.5 text-[11px] text-[#3c4b61]"
              >
                {chip.lob === NO_LOB ? t("No LoB") : chip.lob}{" "}
                <b className="text-[#08254c]">{formatCount(chip.count)}</b>
              </span>
            ))}
          </div>
        ) : (
          <div className="mt-1 text-[22px] font-semibold leading-none text-[#08254c]">
            {NO_VALUE}
          </div>
        )}
      </div>
    </div>
  );
}

/** `emptyLabel` distingue "não há ativos" de "o filtro não achou nada". */
function ScopeItemsTable({ rows, emptyLabel }: { rows: ScopeItemDetailRow[]; emptyLabel: string }) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto">
      <table data-testid="scope-items-table" className="w-full min-w-[960px] border-collapse text-left">
        <thead>
          <tr className="border-b border-[#eef1f5] font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
            <th className="px-3 py-2 font-medium">{t("Scope Item")}</th>
            <th className="px-3 py-2 font-medium">{t("LoB")}</th>
            <th className="px-3 py-2 font-medium">{t("Decision")}</th>
            <th className="px-3 py-2 font-medium">{t("Confidence")}</th>
            <th className="px-3 py-2 font-medium">{t("Clean Core")}</th>
            <th className="px-3 py-2 font-medium">{t("Curation note")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
              <tr
                key={row.scopeItemId}
                data-testid={`scope-item-row-${row.scopeItemId}`}
                className="border-b border-[#eef1f5] text-[12px] text-[#3c4b61] last:border-b-0"
              >
                <td className="px-3 py-2">
                  <span className="font-mono font-semibold text-[#08254c]">{row.scopeItemId}</span>
                  {row.name && <span className="text-[#596b85]"> · {row.name}</span>}
                </td>
                <td className="px-3 py-2">{row.lob ?? NO_VALUE}</td>
                <td className="px-3 py-2">
                  {/* A lista só tem ativos; a coluna existe porque a EF a desenha. */}
                  <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                    {t("Active")}
                  </span>
                </td>
                <td className="px-3 py-2">
                  {row.confidence ? t(confidenceLabel(row.confidence)) : NO_VALUE}
                </td>
                <td className="px-3 py-2">
                  {row.cleanCore && (
                    <span data-testid="clean-core-flag" className="text-[#c77700]">
                      ⚠ {t("yes")}
                    </span>
                  )}
                </td>
                <td
                  data-testid="curation-note"
                  className="max-w-[420px] whitespace-pre-line px-3 py-2"
                >
                  {row.note ?? NO_VALUE}
                </td>
              </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={6} className="px-3 py-6 text-center text-[12px] text-[#7b8799]">
                {emptyLabel}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
